import { useEffect } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../provider/AuthProvider";
import { useInstalledApps } from "../provider/InstalledAppsProvider";
import { usePresence } from "../provider/PresenceProvider";
import useTrackPageAccess from "../hooks/useTrackPageAccess";
import AiDisabledNotice from "../pages/components/AiDisabledNotice";

export const ProtectedRoute = ({ allowedGroups, requiredApp, requiresAi }) => {
  const { userdetails, isAuthLoading } = useAuth();
  const { isAppInstalled, isAppTrialExpired, aiEnabled, isAppsLoading } = useInstalledApps();
  const { setCurrentModule } = usePresence();
  const location = useLocation();

  // Audit page access for installable modules (no-op when no requiredApp)
  useTrackPageAccess(requiredApp || "", location.pathname);

  // Propagate current module into presence so heartbeat carries it
  useEffect(() => {
    setCurrentModule?.(requiredApp || "");
  }, [requiredApp, setCurrentModule]);

  if (isAuthLoading) return null;

  // userdetails is present for both DRF-token users (login) and JWT-cookie users (register).
  // It is cleared on logout, making it the reliable client-side auth indicator.
  if (!userdetails) return <Navigate to="/login" />;

  if (requiredApp) {
    if (isAppsLoading) {
      // The provider fetches on mount and on every company-updated event;
      // calling refresh here on each render while loading forced a second
      // (and third...) fetch per route change. Just wait.
      return null;
    }
    const isInstalled = isAppInstalled(requiredApp);
    if (!isInstalled) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.log(`[ProtectedRoute] Blocking access to ${requiredApp} - not installed`);
      }
      return <Navigate to="/unauthorized" replace state={{ reason: 'module_not_installed', module: requiredApp }} />;
    }
    if (isAppTrialExpired(requiredApp)) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.log(`[ProtectedRoute] Blocking access to ${requiredApp} - trial expired`);
      }
      return <Navigate to="/trial-expired" replace />;
    }
  }

  if (allowedGroups && !userdetails?.is_superuser) {
    const userGroups = userdetails?.group_names || [];
    const hasAccess = allowedGroups.some(group => userGroups.includes(group));
    if (!hasAccess) return <Navigate to="/unauthorized" replace />;
  }

  // Premium AI gate — after app/group checks so the standard notices win.
  if (requiresAi) {
    if (isAppsLoading) return null;
    if (!aiEnabled) return <AiDisabledNotice variant="page" />;
  }

  return <Outlet />;
};
